import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { Add, InfoOutlined, KeyboardArrowDown, KeyboardArrowUp, RemoveCircleOutline } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import AuditDynamicFormActions from './AuditDynamicFormActions';
import AuditDynamicFormErrorDialog from './AuditDynamicFormErrorDialog';
import AuditDynamicFormTopBar from './AuditDynamicFormTopBar';
import { OCR_GUIDE_ROW_HEIGHT, mergeOutlinedInputStyles, receiptOutlinedInputStyles } from './AuditDynamicFormConstants';
import VendingSalesFormSync from './VendingSalesFormSync';
import auditService from '../../services/auditService';
import { normalizeTimePart } from '../../features/audit/fixOcr/utils/nonTicketReportDateUtils';
import { sanitizeVendingSalesIntegerInput } from '../../features/audit/fixOcr/utils/vendingSalesInputUtils';
import { formatTimeHhMmSsInput } from '../../utils/formatters/timeFormatters';
import type { StoreLocation } from '../../types/services/auth';
import type { VendingInventoryCompletedPackRow } from '../../types/vendingInventoryComparisonTypes';
import VendingInventoryMultiPackRows from './VendingInventoryMultiPackRows';

interface VendingShiftReportDynamicFormProps {
  initialValues: any;
  onSubmit: (data: any) => void;
  isSubmitting: boolean;
  savingAction?: 'reject' | 'revert' | 'save' | null;
  auditStatus: string;
  queueId?: string;
  onFormDataUpdate: (data: any) => void;
  onSwitchModes: (data: any, mode: 'form' | 'json') => void;
  onRevertToReady: () => void;
  storeName: string;
  storeLocations?: StoreLocation[];
  gamesData?: Map<string, number> | null;
  hoveredRowIndex?: number | null;
  onRowHover?: (index: number | null) => void;
  onGuideLineElementHover?: (element: HTMLElement | null) => void;
  onGuideGridOriginElementChange?: (element: HTMLElement | null) => void;
  vendingInventoryNewPackKeys?: string[];
  vendingInventoryCompletedPacks?: VendingInventoryCompletedPackRow[];
}

const moneyValue = (value: unknown): number | '' => {
  if (value === null || value === undefined || value === '') return '';
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : '';
};

const intValue = (value: unknown): number | '' => {
  if (value === null || value === undefined || value === '') return '';
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : '';
};

const DEFAULT_VENDING_BIN_COUNT = 24;

const normalizeVendingBinCount = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) {
    return fallback;
  }

  return Math.min(parsed, 500);
};

const toOptionalNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const stepIntegerValue = (value: unknown, delta: number): string => {
  const current = toOptionalNumber(value) ?? 0;
  return String(Math.max(0, current + delta));
};

const getShiftReportCalculatedAmount = (
  game: unknown,
  countValue: unknown,
  gamesData: Map<string, number> | null | undefined,
): number => {
  const gameId = game == null ? '' : String(game).trim();
  const unitPrice = gameId && gamesData ? Number(gamesData.get(gameId) || 0) : 0;
  const quantity = toOptionalNumber(countValue) ?? 0;
  const calculated = unitPrice * quantity;
  return Number.isFinite(calculated) ? Number(calculated.toFixed(2)) : 0;
};

const firstNonEmptyString = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (trimmed) return trimmed;
  }

  return '';
};

const firstNormalizedTime = (...values: unknown[]): string => {
  for (const value of values) {
    const normalized = normalizeTimePart(value);
    if (normalized) return normalized;
  }

  return '';
};

const formatPackCandidateDate = (value: unknown): string => {
  if (typeof value !== 'string' || !value.trim()) return '-';
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return value;
  return `${match[2]}/${match[3]}/${match[1]}`;
};

const formatPackCandidateLocation = (candidate: any): string => {
  const location = candidate?.pack_location || candidate?.last_backstock_location;
  if (!location) return '-';
  const normalized = String(location).trim();
  if (!normalized) return '-';

  const backstockState = String(candidate?.backstock_state || '').trim().toLowerCase();
  const backstockLocations = new Set(['cashier', 'locker', 'vending']);
  if (backstockState && backstockState !== 'none' && backstockLocations.has(normalized.toLowerCase())) {
    return `backstock ${normalized}`;
  }

  return normalized;
};

const buildDefaultValues = (values: any) => {
  const parsedData = values?.parsed_data ?? {};
  const finalJson = parsedData?.final_json ?? {};
  const nestedShiftFinalJson = { ...finalJson };
  delete nestedShiftFinalJson.lines;
  delete nestedShiftFinalJson.scratcher_lines;
  delete nestedShiftFinalJson.draw_games;
  const salesLines = Array.isArray(finalJson?.sales?.lines)
    ? finalJson.sales.lines
    : (Array.isArray(finalJson?.sales?.scratcher_lines) ? finalJson.sales.scratcher_lines : []);
  const inventoryLines = Array.isArray(finalJson?.inventory?.lines) ? finalJson.inventory.lines : [];
  const drawGames = Array.isArray(finalJson?.sales?.draw_games) ? finalJson.sales.draw_games : [];
  const configuredBinCount = normalizeVendingBinCount(
    values?.vending_bin_count
      ?? parsedData?.vending_bin_count
      ?? finalJson?.vending_bin_count,
    Math.max(salesLines.length, inventoryLines.length, DEFAULT_VENDING_BIN_COUNT),
  );

  const normalizedSalesLines = salesLines.map((line: any, index: number) => ({
    ...line,
    bin: intValue(line?.bin) || index + 1,
    game: intValue(line?.game),
    count: intValue(line?.count),
    amount: moneyValue(line?.amount),
  }));
  const paddedSalesLines = Array.from({ length: Math.max(normalizedSalesLines.length, configuredBinCount, 1) }, (_, index) => ({
    ...(normalizedSalesLines[index] ?? createEmptySalesLine(index + 1)),
    bin: index + 1,
  }));

  const normalizedInventoryLines = inventoryLines.map((line: any, index: number) => ({
    ...line,
    bin: intValue(line?.bin) || index + 1,
    game: intValue(line?.game),
    price: moneyValue(line?.price ?? line?.ppt),
    quantity: intValue(line?.quantity ?? line?.count),
    value: moneyValue(line?.value ?? line?.amount),
    selected_pack_id: intValue(line?.selected_pack_id ?? line?.pack_id ?? line?.pack),
    required_pack_count: intValue(line?.required_pack_count),
    pack_capacity: intValue(line?.pack_capacity),
    selected_packs: Array.isArray(line?.selected_packs)
      ? line.selected_packs.map((pack: any) => ({
          pack_id: pack?.pack_id ?? null,
          remaining_qty: intValue(pack?.remaining_qty ?? pack?.quantity),
        }))
      : [],
    pack_candidates: Array.isArray(line?.pack_candidates) ? line.pack_candidates : [],
  }));
  const paddedInventoryLines = Array.from({ length: Math.max(normalizedInventoryLines.length, configuredBinCount, 1) }, (_, index) => ({
    ...(normalizedInventoryLines[index] ?? createEmptyInventoryLine(index + 1)),
    bin: index + 1,
  }));

  return {
    action: values?.action ?? 'finalize',
    notes: values?.notes ?? '',
    confidence_score: Number(values?.confidence_score ?? finalJson?.confidence ?? 0),
    store_location_id: values?.store_location_id ?? null,
    include_inventory_report: Boolean(values?.include_inventory_report ?? parsedData?.include_inventory_report ?? finalJson?.include_inventory_report ?? false),
    location_key: firstNonEmptyString(values?.location_key, parsedData?.location_key, finalJson?.location_key) || 'VM-01',
    shift_date: firstNonEmptyString(values?.shift_date, parsedData?.shift_date, finalJson?.shift_date, finalJson?.report_date),
    shift_start_time: firstNormalizedTime(
      values?.shift_start_time,
      values?.report_time,
      parsedData?.report_time,
      parsedData?.shift_time,
      finalJson?.to_datetime,
      finalJson?.report_time,
      finalJson?.shift_time,
      finalJson?.time,
    ),
    parsed_data: {
      ...parsedData,
      final_json: {
        ...nestedShiftFinalJson,
        report_type: 'vending_shift_report',
        sales: {
          ...(finalJson?.sales ?? {}),
          report_type: 'vending_sales',
          lines: paddedSalesLines,
          draw_games: drawGames.map((line: any) => ({
            ...line,
            name: line?.name ?? line?.item_name ?? 'DRAW GAME',
            count: intValue(line?.count),
            amount: moneyValue(line?.amount),
          })),
        },
        inventory: {
          ...(finalJson?.inventory ?? {}),
          report_type: 'vending_inventory',
          lines: paddedInventoryLines,
        },
      },
    },
  };
};

const createEmptySalesLine = (bin: number) => ({
  bin,
  game: '',
  count: '',
  amount: '',
});

const createEmptyInventoryLine = (bin: number) => ({
  bin,
  game: '',
  price: '',
  quantity: '',
  value: '',
  selected_pack_id: '',
  required_pack_count: '',
  pack_capacity: '',
  selected_packs: [],
  pack_candidates: [],
});

const cloneInventoryLine = (line: any) => ({
  ...line,
  selected_packs: Array.isArray(line?.selected_packs)
    ? line.selected_packs.map((pack: any) => ({ ...pack }))
    : [],
  pack_candidates: Array.isArray(line?.pack_candidates)
    ? line.pack_candidates.map((candidate: any) => ({ ...candidate }))
    : [],
});

const tableHeaderSx = {
  fontSize: 12,
  fontWeight: 800,
  color: '#2b2825',
};

const salesGridColumns = '72px 1fr 112px 132px 36px';
const drawGameGridColumns = 'minmax(120px, 1fr) 82px 96px';
const inventoryGridColumns = '64px minmax(196px, 1.08fr) 96px 104px 20px 58px';

const guideRowSx = {
  boxSizing: 'border-box',
  height: OCR_GUIDE_ROW_HEIGHT,
  minHeight: OCR_GUIDE_ROW_HEIGHT,
  maxHeight: OCR_GUIDE_ROW_HEIGHT,
  lineHeight: `${OCR_GUIDE_ROW_HEIGHT}px`,
  overflow: 'hidden',
};

const receiptInputBaseSx = {
  ...receiptOutlinedInputStyles['& .MuiInputBase-input'],
  fontSize: 17,
  fontFamily: '"Courier Prime", "Courier New", monospace',
  fontWeight: 400,
  color: '#222B45',
};

const receiptInputSx = mergeOutlinedInputStyles({
  ...receiptOutlinedInputStyles,
  width: '100%',
  color: '#222B45',
  '& .MuiInputBase-input': receiptInputBaseSx,
});

const VendingShiftReportDynamicForm = forwardRef<any, VendingShiftReportDynamicFormProps>(({
  initialValues,
  onSubmit,
  isSubmitting,
  savingAction,
  auditStatus,
  queueId,
  onFormDataUpdate,
  onSwitchModes,
  onRevertToReady,
  storeName,
  storeLocations,
  gamesData,
  hoveredRowIndex,
  onRowHover,
  onGuideLineElementHover,
  onGuideGridOriginElementChange,
  vendingInventoryNewPackKeys,
  vendingInventoryCompletedPacks,
}, ref) => {
  const defaultValues = useMemo(() => buildDefaultValues(initialValues), [initialValues]);
  const [errorPopupOpen, setErrorPopupOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [includeInventoryReport, setIncludeInventoryReport] = useState(Boolean(defaultValues.include_inventory_report));
  const [packMenuState, setPackMenuState] = useState<{
    anchorEl: HTMLElement;
    rowIndex: number;
    candidates: any[] | null;
    loading: boolean;
    error: string | null;
  } | null>(null);
  const { control, register, handleSubmit, getValues, setValue } = useForm<any>({ defaultValues });
  const { fields: salesFields, append: appendSales, remove: removeSales, insert: insertSales } = useFieldArray({ control, name: 'parsed_data.final_json.sales.lines' });
  const { fields: drawGameFields } = useFieldArray({ control, name: 'parsed_data.final_json.sales.draw_games' });
  const { fields: inventoryFields, append: appendInventory, remove: removeInventory, insert: insertInventory } = useFieldArray({ control, name: 'parsed_data.final_json.inventory.lines' });

  const watchedSalesLines = useWatch({ control, name: 'parsed_data.final_json.sales.lines' });
  const watchedDrawGames = useWatch({ control, name: 'parsed_data.final_json.sales.draw_games' });
  const watchedInventoryLines = useWatch({ control, name: 'parsed_data.final_json.inventory.lines' });
  const watchedShiftDate = useWatch({ control, name: 'shift_date' });

  useImperativeHandle(ref, () => ({ getValues, setValue }), [getValues, setValue]);

  const newPackKeySet = useMemo(() => (
    new Set(Array.isArray(vendingInventoryNewPackKeys) ? vendingInventoryNewPackKeys : [])
  ), [vendingInventoryNewPackKeys]);

  const completedPacks = useMemo(() => (
    Array.isArray(vendingInventoryCompletedPacks) ? vendingInventoryCompletedPacks : []
  ), [vendingInventoryCompletedPacks]);

  const totals = useMemo(() => {
    const salesLines = Array.isArray(watchedSalesLines) ? watchedSalesLines : [];
    const drawGames = Array.isArray(watchedDrawGames) ? watchedDrawGames : [];
    const inventoryLines = Array.isArray(watchedInventoryLines) ? watchedInventoryLines : [];

    return {
      salesCount: salesLines.reduce((sum, line) => sum + (toOptionalNumber(line?.count) ?? 0), 0),
      salesAmount: salesLines.reduce((sum, line) => sum + (toOptionalNumber(line?.amount) ?? 0), 0),
      drawCount: drawGames.reduce((sum, line) => sum + (toOptionalNumber(line?.count) ?? 0), 0),
      drawAmount: drawGames.reduce((sum, line) => sum + (toOptionalNumber(line?.amount) ?? 0), 0),
      inventoryQuantity: inventoryLines.reduce((sum, line) => sum + (toOptionalNumber(line?.quantity) ?? 0), 0),
      inventoryValue: inventoryLines.reduce((sum, line) => {
        const explicitValue = toOptionalNumber(line?.value);
        if (explicitValue !== null) return sum + explicitValue;
        const gameId = line?.game != null ? String(line.game).trim() : '';
        const costPerTicket = gameId && gamesData ? Number(gamesData.get(gameId) || 0) : 0;
        return sum + costPerTicket * (toOptionalNumber(line?.quantity) ?? 0);
      }, 0),
    };
  }, [gamesData, watchedDrawGames, watchedInventoryLines, watchedSalesLines]);

  const reindexNestedBins = useCallback((path: 'sales' | 'inventory') => {
    const currentLines = getValues(`parsed_data.final_json.${path}.lines`);
    if (!Array.isArray(currentLines)) return;

    currentLines.forEach((line: any, index: number) => {
      if (line?.bin === index + 1) return;
      setValue(`parsed_data.final_json.${path}.lines.${index}.bin`, index + 1, {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      });
    });
  }, [getValues, setValue]);

  const handleRemoveInventory = useCallback((index: number) => {
    removeInventory(index);
    Promise.resolve().then(() => reindexNestedBins('inventory'));
  }, [removeInventory, reindexNestedBins]);

  const handleSwitchToJson = () => onSwitchModes(getValues(), 'json');
  const handleReject = () => onSubmit({ ...getValues(), action: 'reject' });
  const handleFormSubmit = (data: any) => onSubmit(data);
  const handleSubmitError = (errors: any) => {
    setErrorMessage(JSON.stringify(errors, null, 2));
    setErrorPopupOpen(true);
  };

  const renderReceiptNumber = (
    name: string,
    align: 'center' | 'right',
    step = '1',
    testId?: string,
    hideSpinButtons = false,
  ) => (
    <TextField
      {...register(name)}
      type="number"
      size="small"
      inputProps={{ step, 'data-testid': testId }}
      sx={mergeOutlinedInputStyles({
        ...receiptInputSx,
        '& .MuiInputBase-input': {
          ...receiptInputBaseSx,
          textAlign: align,
          paddingRight: align === 'right' ? '4px !important' : undefined,
        },
        ...(hideSpinButtons ? {
          '& input[type=number]': {
            MozAppearance: 'textfield',
          },
          '& input[type=number]::-webkit-outer-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
          '& input[type=number]::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
        } : {}),
      })}
    />
  );

  const renderReceiptText = (
    name: string,
    testId?: string,
  ) => (
    <TextField
      {...register(name)}
      size="small"
      inputProps={{ 'data-testid': testId }}
      sx={mergeOutlinedInputStyles({
        ...receiptInputSx,
        '& .MuiInputBase-input': {
          ...receiptInputBaseSx,
          textAlign: 'left',
          paddingLeft: '8px !important',
        },
      })}
    />
  );

  const getInventoryGamePackValue = (line: any): string => {
    const gamePart = line?.game === null || line?.game === undefined ? '' : String(line.game).trim();
    const packCandidate = line?.selected_pack_id ?? line?.pack_id ?? line?.pack;
    const packPart = packCandidate === null || packCandidate === undefined || packCandidate === ''
      ? ''
      : String(packCandidate).trim();
    return gamePart ? `${gamePart}-${packPart}` : '';
  };

  const updateInventoryGamePack = useCallback((index: number, rawValue: string) => {
    const cleanValue = rawValue.replace(/[^\d-]/g, '');
    const parts = cleanValue.split('-');
    const gamePart = (parts[0] || '').replace(/[^\d]/g, '').slice(0, 6);
    const packPart = (parts[1] || '').replace(/[^\d]/g, '').slice(0, 7);

    setValue(`parsed_data.final_json.inventory.lines.${index}.game`, gamePart, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setValue(`parsed_data.final_json.inventory.lines.${index}.selected_pack_id`, packPart, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setValue(`parsed_data.final_json.inventory.lines.${index}.pack`, packPart, {
      shouldDirty: true,
      shouldTouch: false,
      shouldValidate: false,
    });
  }, [setValue]);

  const handleOpenPackMenu = useCallback(async (
    event: React.MouseEvent<HTMLElement>,
    index: number,
    line: any,
  ) => {
    event.stopPropagation();
    const initialCandidates = Array.isArray(line?.pack_candidates) ? line.pack_candidates : [];
    setPackMenuState({
      anchorEl: event.currentTarget,
      rowIndex: index,
      candidates: initialCandidates,
      loading: false,
      error: null,
    });

    const gameId = toOptionalNumber(line?.game);
    if (!queueId || !watchedShiftDate || gameId === null) {
      return;
    }

    setPackMenuState((previous) => previous && previous.rowIndex === index ? { ...previous, loading: true, error: null } : previous);
    try {
      const response = await auditService.getVendingInventoryPackRecommendations(queueId, {
        report_date: String(watchedShiftDate),
        game_id: gameId,
        bin: toOptionalNumber(line?.bin),
        quantity: toOptionalNumber(line?.quantity),
        value: toOptionalNumber(line?.value),
      });
      const nextCandidates = Array.isArray(response.candidates) ? response.candidates : [];
      setValue(`parsed_data.final_json.inventory.lines.${index}.pack_candidates`, nextCandidates, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
      setPackMenuState((previous) => previous && previous.rowIndex === index
        ? { ...previous, candidates: nextCandidates, loading: false, error: null }
        : previous);
    } catch (error: any) {
      setPackMenuState((previous) => previous && previous.rowIndex === index
        ? { ...previous, loading: false, error: error?.response?.data?.detail || error?.message || 'Could not load pack recommendations' }
        : previous);
    }
  }, [queueId, setValue, watchedShiftDate]);

  const handleSelectPack = useCallback((index: number, packId: number | null | undefined) => {
    const nextValue = packId == null ? '' : String(packId);
    setValue(`parsed_data.final_json.inventory.lines.${index}.selected_pack_id`, nextValue, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setValue(`parsed_data.final_json.inventory.lines.${index}.pack`, nextValue, {
      shouldDirty: true,
      shouldTouch: false,
      shouldValidate: false,
    });
    setPackMenuState(null);
  }, [setValue]);

  const normalizedStatus = String(auditStatus || '').toLowerCase();
  const canRecommendPacks = normalizedStatus === 'ready';
  const handleGuideRowMouseEnter = (
    rowIndex: number | null,
    event: React.MouseEvent<HTMLElement>,
  ) => {
    if (rowIndex !== null) {
      onRowHover?.(rowIndex);
    }
    onGuideLineElementHover?.(event.currentTarget);
  };
  const handleGuideRowMouseLeave = () => {
    onRowHover?.(null);
    onGuideLineElementHover?.(null);
  };
  const setGuideGridOriginRowRef = useCallback((element: HTMLDivElement | null) => {
    onGuideGridOriginElementChange?.(element);
  }, [onGuideGridOriginElementChange]);

  return (
    <form onSubmit={handleSubmit(handleFormSubmit, handleSubmitError)}>
      <VendingSalesFormSync control={control} getValues={getValues} onFormDataUpdate={onFormDataUpdate} />
      <AuditDynamicFormTopBar
        storeName={storeName}
        initialValues={initialValues}
        onSwitchToJson={handleSwitchToJson}
      />

      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <Box
          sx={{
            boxSizing: 'border-box',
            width: '100%',
            maxWidth: 940,
            px: { xs: 2, sm: 3 },
            py: 3,
            border: '1px solid #d0aa6f',
            bgcolor: '#fffaf0',
            color: '#2b2825',
            boxShadow: '0 10px 24px rgba(80, 56, 26, 0.08)',
            fontFamily: '"Courier Prime", "Courier New", monospace',
          }}
        >
          <Box sx={{ height: 25, mb: 1 }} />
          <Typography sx={{ textAlign: 'center', fontSize: 23, fontWeight: 800, lineHeight: 1.2, mt: 0.75, mb: 2 }}>
            VENDING SHIFT REPORT
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 1.5, mb: 2.5 }}>
            <Controller
              name="location_key"
              control={control}
              render={({ field }) => (
                <TextField select label="Location" size="small" required {...field} value={field.value ?? ''}>
                  <MenuItem value="">Select location</MenuItem>
                  {(storeLocations || []).map((location: any) => (
                    <MenuItem key={location.location_key || location.id} value={location.location_key}>
                      {location.location_name || location.location_key || location.name}
                    </MenuItem>
                  ))}
                  <MenuItem value="VM-01">VM-01</MenuItem>
                </TextField>
              )}
            /> 
            <Controller
              name="shift_date"
              control={control}
              render={({ field }) => (
                <TextField label="Report Date" size="small" type="date" required InputLabelProps={{ shrink: true }} {...field} value={field.value ?? ''} />
              )}
            />
            <Controller
              name="shift_start_time"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Report Time"
                  size="small"
                  required
                  {...field}
                  value={field.value ?? ''}
                  onChange={(event) => field.onChange(formatTimeHhMmSsInput(event.target.value))}
                  inputProps={{ 'data-testid': 'shift-report-time' }}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Box>
          <Controller
            name="include_inventory_report"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                sx={{ mb: 1.5 }}
                control={(
                  <Checkbox
                    checked={includeInventoryReport}
                    onChange={(event) => {
                      setIncludeInventoryReport(event.target.checked);
                      field.onChange(event.target.checked);
                    }}
                    size="small"
                  />
                )}
                label="Include inventory report"
              />
            )}
          />

          <Typography sx={{ fontWeight: 800, fontSize: 16, mb: 1 }}>SALES</Typography>
          <Box
            sx={{
              ...guideRowSx,
              display: 'grid',
              gridTemplateColumns: salesGridColumns,
              columnGap: 1.5,
              alignItems: 'center',
              borderBottom: '1px dashed #d6b982',
              mb: 0.5,
            }}
          >
            {['BIN', 'GAME', 'COUNT', 'AMOUNT', ''].map((label) => <Typography key={label || 'sales-actions'} sx={tableHeaderSx}>{label}</Typography>)}
          </Box>
          {salesFields.map((field, index) => {
            const line = Array.isArray(watchedSalesLines) ? watchedSalesLines[index] : null;
            const isSalesRowHovered = hoveredRowIndex === index + 1;
            const computedSalesAmount = getShiftReportCalculatedAmount(line?.game, line?.count, gamesData);

            return (
              <React.Fragment key={field.id}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: 'transparent',
                    cursor: 'pointer',
                    opacity: 1,
                    height: 0,
                    minHeight: 0,
                    overflow: 'visible',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  <Box
                    className="add-btn"
                    sx={{
                      position: 'absolute',
                      top: -10,
                      left: 0,
                      right: 0,
                      width: 'calc(100% - 20px)',
                      height: 20,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      opacity: isSalesRowHovered ? 1 : 0,
                      transition: 'opacity 0.12s ease-in-out',
                    }}
                  >
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label="Insert sales row"
                      onClick={() => {
                        insertSales(index, createEmptySalesLine(index + 1));
                        Promise.resolve().then(() => reindexNestedBins('sales'));
                      }}
                      sx={{ p: 0, width: 20, height: 20 }}
                    >
                      <Add sx={{ fontSize: 20 }} />
                    </IconButton>
                    <Divider
                      sx={{
                        borderColor: '#A3AED099',
                        borderWidth: '0.5px',
                        borderStyle: 'dashed',
                        width: '100%',
                      }}
                    />
                  </Box>
                </Box>

                <Box
                  ref={index === 0 ? setGuideGridOriginRowRef : undefined}
                  key={field.id}
                  onMouseEnter={(event) => handleGuideRowMouseEnter(index + 1, event)}
                  onMouseLeave={handleGuideRowMouseLeave}
                  sx={{
                    ...guideRowSx,
                    display: 'grid',
                    gridTemplateColumns: salesGridColumns,
                    columnGap: 1.5,
                    alignItems: 'center',
                    borderBottom: '1px dashed #d6b982',
                    fontFamily: 'inherit',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    {renderReceiptNumber(`parsed_data.final_json.sales.lines.${index}.bin`, 'center', '1', `sales-bin-${index}`, true)}
                  </Box>
                  <Controller
                    name={`parsed_data.final_json.sales.lines.${index}.game`}
                    control={control}
                    render={({ field: gameField }) => (
                      <TextField
                        {...gameField}
                        value={gameField.value ?? ''}
                        onChange={(event) => {
                          const nextGame = sanitizeVendingSalesIntegerInput(event.target.value).slice(0, 6);
                          gameField.onChange(nextGame);
                          const nextAmount = getShiftReportCalculatedAmount(nextGame, line?.count, gamesData);
                          setValue(`parsed_data.final_json.sales.lines.${index}.amount`, nextAmount, {
                            shouldDirty: true,
                            shouldTouch: false,
                            shouldValidate: false,
                          });
                        }}
                        size="small"
                        inputProps={{ 'data-testid': `sales-game-${index}` }}
                        sx={mergeOutlinedInputStyles({
                          ...receiptInputSx,
                          '& .MuiInputBase-input': {
                            ...receiptInputBaseSx,
                            textAlign: 'center',
                            paddingRight: '4px !important',
                          },
                        })}
                      />
                    )}
                  />
                  <Controller
                    name={`parsed_data.final_json.sales.lines.${index}.count`}
                    control={control}
                    render={({ field: countField }) => (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, height: '100%' }}>
                        <TextField
                          {...countField}
                          value={countField.value ?? ''}
                          onChange={(event) => {
                            const nextCount = sanitizeVendingSalesIntegerInput(event.target.value).slice(0, 6);
                            countField.onChange(nextCount);
                            const nextAmount = getShiftReportCalculatedAmount(line?.game, nextCount, gamesData);
                            setValue(`parsed_data.final_json.sales.lines.${index}.amount`, nextAmount, {
                              shouldDirty: true,
                              shouldTouch: false,
                              shouldValidate: false,
                            });
                          }}
                          size="small"
                          inputProps={{ 'data-testid': `sales-count-${index}` }}
                          sx={mergeOutlinedInputStyles({
                            ...receiptInputSx,
                            '& .MuiInputBase-input': {
                              ...receiptInputBaseSx,
                              textAlign: 'right',
                              paddingRight: '4px !important',
                            },
                            width: 'calc(100% - 44px)',
                            flex: '0 0 calc(100% - 44px)',
                          })}
                        />
                        <Box
                          className="count-stepper"
                          sx={{
                            flex: '0 0 44px',
                            width: 44,
                            height: OCR_GUIDE_ROW_HEIGHT - 2,
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            gap: 0.25,
                            opacity: isSalesRowHovered ? 1 : 0,
                            pointerEvents: isSalesRowHovered ? 'auto' : 'none',
                            transition: 'opacity 0.12s ease-in-out',
                            zIndex: 3,
                          }}
                        >
                          <IconButton
                            aria-label="Decrease sales count"
                            size="small"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={(event) => {
                              event.stopPropagation();
                              const nextCount = stepIntegerValue(countField.value, -1);
                              countField.onChange(nextCount);
                              const nextAmount = getShiftReportCalculatedAmount(line?.game, nextCount, gamesData);
                              setValue(`parsed_data.final_json.sales.lines.${index}.amount`, nextAmount, {
                                shouldDirty: true,
                                shouldTouch: false,
                                shouldValidate: false,
                              });
                            }}
                            sx={{
                              p: 0,
                              width: 22,
                              height: 22,
                              minWidth: 22,
                              bgcolor: 'rgba(255, 250, 240, 0.95)',
                              border: '1px solid #b98542',
                              borderRadius: 0.75,
                              color: '#2b2825',
                              '&:hover': { bgcolor: '#f4e6cc' },
                            }}
                          >
                            <KeyboardArrowDown sx={{ fontSize: 20 }} />
                          </IconButton>
                          <IconButton
                            aria-label="Increase sales count"
                            size="small"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={(event) => {
                              event.stopPropagation();
                              const nextCount = stepIntegerValue(countField.value, 1);
                              countField.onChange(nextCount);
                              const nextAmount = getShiftReportCalculatedAmount(line?.game, nextCount, gamesData);
                              setValue(`parsed_data.final_json.sales.lines.${index}.amount`, nextAmount, {
                                shouldDirty: true,
                                shouldTouch: false,
                                shouldValidate: false,
                              });
                            }}
                            sx={{
                              p: 0,
                              width: 22,
                              height: 22,
                              minWidth: 22,
                              bgcolor: 'rgba(255, 250, 240, 0.95)',
                              border: '1px solid #b98542',
                              borderRadius: 0.75,
                              color: '#2b2825',
                              '&:hover': { bgcolor: '#f4e6cc' },
                            }}
                          >
                            <KeyboardArrowUp sx={{ fontSize: 20 }} />
                          </IconButton>
                        </Box>
                      </Box>
                    )}
                  />
                  <TextField
                    value={Number.isFinite(computedSalesAmount) ? computedSalesAmount.toFixed(2) : '0.00'}
                    placeholder="0.00"
                    size="small"
                    disabled
                    inputProps={{ 'data-testid': `sales-amount-${index}` }}
                    sx={mergeOutlinedInputStyles({
                      ...receiptInputSx,
                      '& .MuiInputBase-input': {
                        ...receiptInputBaseSx,
                        textAlign: 'right',
                        paddingRight: '4px !important',
                        WebkitTextFillColor: '#222B45',
                      },
                    })}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <IconButton
                      size="small"
                      aria-label="Remove sales row"
                      onClick={() => {
                        removeSales(index);
                        Promise.resolve().then(() => reindexNestedBins('sales'));
                      }}
                      sx={{ p: 0, width: 20, height: 20 }}
                    >
                      <RemoveCircleOutline sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Box>
                </Box>
              </React.Fragment>
            );
          })}
          <IconButton
            size="small"
            aria-label="Add sales row"
            onClick={() => {
              const nextIndex = Array.isArray(watchedSalesLines) ? watchedSalesLines.length : salesFields.length;
              appendSales(createEmptySalesLine(nextIndex + 1));
            }}
            sx={{ mt: 0.25, width: 28, height: 28 }}
          >
            <Add sx={{ fontSize: 18 }} />
          </IconButton>

          <Box sx={{ ...guideRowSx, display: 'grid', gridTemplateColumns: salesGridColumns, columnGap: 1.5, alignItems: 'center', mt: 0.5 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 800 }}>Total</Typography>
            <Box />
            <Typography sx={{ fontSize: 13, fontWeight: 800, textAlign: 'right' }}>{totals.salesCount}</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 800, textAlign: 'right' }}>{totals.salesAmount.toFixed(2)}</Typography>
            <Box />
          </Box>

          {drawGameFields.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontWeight: 800, fontSize: 14, mb: 0.5 }}>DRAW GAMES</Typography>
              <Box sx={{ ...guideRowSx, display: 'grid', gridTemplateColumns: drawGameGridColumns, columnGap: 1, alignItems: 'center', mb: 0.5 }}>
                {['NAME', 'COUNT', 'AMOUNT'].map((label) => <Typography key={label} sx={tableHeaderSx}>{label}</Typography>)}
              </Box>
              {drawGameFields.map((field, index) => (
                <Box
                  key={field.id}
                  onMouseEnter={(event) => handleGuideRowMouseEnter(null, event)}
                  onMouseLeave={handleGuideRowMouseLeave}
                  sx={{
                    ...guideRowSx,
                    display: 'grid',
                    gridTemplateColumns: drawGameGridColumns,
                    columnGap: 1,
                    alignItems: 'center',
                    borderBottom: '1px dashed transparent',
                  }}
                >
                  {renderReceiptText(`parsed_data.final_json.sales.draw_games.${index}.name`, `draw-name-${index}`)}
                  {renderReceiptNumber(`parsed_data.final_json.sales.draw_games.${index}.count`, 'right', '1', `draw-count-${index}`)}
                  {renderReceiptNumber(`parsed_data.final_json.sales.draw_games.${index}.amount`, 'right', '0.01', `draw-amount-${index}`)}
                </Box>
              ))}
              <Box sx={{ ...guideRowSx, display: 'grid', gridTemplateColumns: drawGameGridColumns, columnGap: 1, alignItems: 'center', mt: 0.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 800 }}>Draw Total</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 800, textAlign: 'right' }}>{totals.drawCount}</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 800, textAlign: 'right' }}>{totals.drawAmount.toFixed(2)}</Typography>
              </Box>
            </Box>
          )}

          {includeInventoryReport && (
            <>
              <Divider sx={{ my: 2, borderColor: '#b98542' }} />
              <Typography
                sx={{
                  height: OCR_GUIDE_ROW_HEIGHT + 12,
                  minHeight: OCR_GUIDE_ROW_HEIGHT + 12,
                  maxHeight: OCR_GUIDE_ROW_HEIGHT + 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  fontWeight: 800,
                  fontSize: 20,
                  lineHeight: 1.2,
                  mb: 0.75,
                }}
              >
                INVENTORY REPORT
              </Typography>
              <Box
                sx={{
                  ...guideRowSx,
                  display: 'grid',
                  gridTemplateColumns: inventoryGridColumns,
                  alignItems: 'center',
                  color: '#2b2825',
                  fontFamily: 'inherit',
                  borderBottom: '1px dashed #d6b982',
                  columnGap: 0.25,
                }}
              >
                <Box sx={{ textAlign: 'center', fontSize: 17, fontWeight: 800, fontFamily: 'inherit' }}>BIN</Box>
                <Box sx={{ textAlign: 'left', fontSize: 17, fontWeight: 800, fontFamily: 'inherit', whiteSpace: 'nowrap', pl: 1 }}>
                  GAME - PACK
                </Box>
                <Box sx={{ textAlign: 'right', fontSize: 17, fontWeight: 800, fontFamily: 'inherit' }}>QTY</Box>
                <Box sx={{ textAlign: 'right', fontSize: 17, fontWeight: 800, fontFamily: 'inherit' }}>VALUE</Box>
                <Box />
                <Box />
              </Box>
              {inventoryFields.map((field, index) => {
                const line = Array.isArray(watchedInventoryLines) ? watchedInventoryLines[index] : null;
                const lineKey = line?.bin != null && line?.game != null ? `${String(line.bin).trim()}:${String(line.game).trim()}` : '';
                const isNewPack = Boolean(lineKey && newPackKeySet.has(lineKey));
                const isInventoryRowHovered = hoveredRowIndex === index + 1;

                return (
                  <React.Fragment key={field.id}>
                    <Box
                      onMouseEnter={() => onRowHover?.(index + 1)}
                      onMouseLeave={() => onRowHover?.(null)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        bgcolor: 'transparent',
                        cursor: 'pointer',
                        opacity: 1,
                        height: 0,
                        minHeight: 0,
                        overflow: 'visible',
                        position: 'relative',
                        zIndex: 1,
                      }}
                    >
                      <Box
                        className="add-btn"
                        sx={{
                          position: 'absolute',
                          top: -10,
                          left: 0,
                          right: 0,
                          width: 'calc(100% - 20px)',
                          height: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          opacity: isInventoryRowHovered ? 1 : 0,
                          transition: 'opacity 0.12s ease-in-out',
                        }}
                      >
                        <IconButton
                          size="small"
                          color="primary"
                          aria-label="Insert inventory row"
                          onClick={() => {
                            const currentInventoryLines = getValues('parsed_data.final_json.inventory.lines');
                            const preservedInventoryLines = Array.isArray(currentInventoryLines)
                              ? currentInventoryLines.map(cloneInventoryLine)
                              : [];
                            insertInventory(index, createEmptyInventoryLine(index + 1));
                            Promise.resolve().then(() => {
                              const restoredInventoryLines = [
                                ...preservedInventoryLines.slice(0, index),
                                createEmptyInventoryLine(index + 1),
                                ...preservedInventoryLines.slice(index),
                              ];
                              setValue('parsed_data.final_json.inventory.lines', restoredInventoryLines, {
                                shouldDirty: true,
                                shouldTouch: false,
                                shouldValidate: false,
                              });
                              reindexNestedBins('inventory');
                            });
                          }}
                          sx={{ p: 0, width: 20, height: 20 }}
                        >
                          <Add sx={{ fontSize: 20 }} />
                        </IconButton>
                        <Divider
                          sx={{
                            borderColor: '#A3AED099',
                            borderWidth: '0.5px',
                            borderStyle: 'dashed',
                            width: '100%',
                          }}
                        />
                      </Box>
                    </Box>
                    <VendingInventoryMultiPackRows
                      field={field}
                      idx={index}
                      line={line}
                      linePathBase={`parsed_data.final_json.inventory.lines.${index}`}
                      rowErrors={null}
                      setValue={setValue}
                      remove={handleRemoveInventory}
                      gamesData={gamesData || null}
                      reportType="vending_shift_report"
                      queueId={queueId}
                      reportDate={String(watchedShiftDate || '')}
                      auditStatus={auditStatus}
                      hoveredRowIndex={hoveredRowIndex ?? null}
                      onRowHover={onRowHover}
                      isNewPack={isNewPack}
                    />
                  </React.Fragment>
                );
              })}
              <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'transparent', cursor: 'pointer', opacity: 1, height: 20 }}>
                <Box
                  className="add-btn"
                  sx={{
                    width: 'calc(100% - 20px)',
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                  }}
                >
                  <IconButton
                    size="small"
                    color="primary"
                    aria-label="Add inventory row"
                    sx={{ p: 0, width: 20, height: 20 }}
                    onClick={() => {
                      const nextIndex = Array.isArray(watchedInventoryLines) ? watchedInventoryLines.length : inventoryFields.length;
                      appendInventory(createEmptyInventoryLine(nextIndex + 1));
                    }}
                  >
                    <Add sx={{ fontSize: 20 }} />
                  </IconButton>
                  <Divider
                    sx={{
                      borderColor: hoveredRowIndex === salesFields.length + inventoryFields.length + 1 ? '#3a3128' : '#d6b982',
                      borderWidth: '0.5px',
                      borderStyle: 'dashed',
                      width: '100%',
                      transition: 'all 0.1s ease-in-out',
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ bgcolor: '#fff' }}>
                <Box sx={{ height: 8 }} />
                <Box sx={{ height: 1, bgcolor: '#A3AED099' }} />
                <Box
                  sx={{
                    ...guideRowSx,
                    display: 'grid',
                    gridTemplateColumns: inventoryGridColumns,
                    alignItems: 'center',
                    px: 0,
                    bgcolor: '#fff',
                    columnGap: 0.25,
                  }}
                >
                  <Box sx={{ textAlign: 'center', px: 0.5 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#222B45' }}>Total</Typography>
                  </Box>
                  <Box />
                  <Box sx={{ textAlign: 'right', px: 0.5 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#222B45' }}>
                      {Number.isFinite(totals.inventoryQuantity) ? String(totals.inventoryQuantity) : '0'}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right', px: 0.5 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#222B45' }}>
                      {Number.isFinite(totals.inventoryValue) ? totals.inventoryValue.toFixed(2) : '0.00'}
                    </Typography>
                  </Box>
                  <Box />
                  <Box />
                </Box>
                <Box sx={{ height: 1, bgcolor: '#A3AED099' }} />
                <Box sx={{ height: 8 }} />
              </Box>

              {completedPacks.length > 0 && (
                <Box sx={{ mt: 0.75, px: 0.5 }}>
                  <Box
                    sx={{
                      borderRadius: 2,
                      border: '1px solid rgba(255, 152, 0, 0.9)',
                      bgcolor: 'rgba(255, 152, 0, 0.08)',
                      px: 1,
                      py: 0.75,
                    }}
                  >
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'rgba(255, 152, 0, 1)', mb: 1 }}>
                      Assumed Completed Packs
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '72px 120px 90px 110px', columnGap: 1, px: 0.5, mb: 0.5 }}>
                      {['Bin', 'GameId', 'Qty', 'Value'].map((label) => (
                        <Typography key={label} sx={{ fontSize: 12, fontWeight: 700, color: 'rgba(34, 43, 69, 0.8)' }}>
                          {label}
                        </Typography>
                      ))}
                    </Box>
                    <Divider sx={{ my: 0.5, borderColor: 'rgba(255, 152, 0, 0.35)' }} />
                    {completedPacks.map((pack, index) => (
                      <React.Fragment key={pack.key}>
                        {index > 0 && <Divider sx={{ my: 0.6, borderColor: 'rgba(255, 152, 0, 0.25)' }} />}
                        <Box sx={{ display: 'grid', gridTemplateColumns: '72px 120px 90px 110px', columnGap: 1, px: 0.5 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#222B45' }}>{pack.bin}</Typography>
                          <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#222B45' }}>{pack.gameId}</Typography>
                          <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#222B45' }}>{pack.quantity ?? '-'}</Typography>
                          <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#222B45' }}>${pack.value ?? '-'}</Typography>
                        </Box>
                      </React.Fragment>
                    ))}
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      <Menu
        anchorEl={packMenuState?.anchorEl ?? null}
        open={Boolean(packMenuState)}
        onClose={() => setPackMenuState(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {packMenuState?.loading ? (
          <MenuItem disabled>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={14} />
              Loading packs
            </Box>
          </MenuItem>
        ) : packMenuState?.error ? (
          <MenuItem disabled>{packMenuState.error}</MenuItem>
        ) : (packMenuState?.candidates || []).length > 0 ? (
          (packMenuState?.candidates || []).map((candidate: any, optionIndex: number) => (
            <MenuItem
              key={`shift-pack-candidate-${packMenuState?.rowIndex}-${optionIndex}-${candidate?.pack_id ?? 'na'}`}
              onClick={() => handleSelectPack(packMenuState?.rowIndex ?? 0, candidate?.pack_id)}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                  {`${candidate?.pack_id ?? '-'} (${formatPackCandidateLocation(candidate)})`}
                </Box>
                <Box
                  component="span"
                  sx={{
                    color: '#374151',
                    fontSize: 12,
                    fontWeight: 600,
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {`${candidate?.status ?? '-'} | Set ${formatPackCandidateDate(candidate?.settled_date ?? candidate?.status_date)} | Act ${formatPackCandidateDate(candidate?.activated_date)} | Rem ${candidate?.latest_remaining_qty ?? '-'}`}
                </Box>
              </Box>
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>No packs</MenuItem>
        )}
      </Menu>

      <AuditDynamicFormActions
        isSubmitting={isSubmitting}
        savingAction={savingAction}
        auditStatus={auditStatus}
        onReject={handleReject}
        onRevertToReady={onRevertToReady}
        submitButtonLabel="Process"
      />
      <AuditDynamicFormErrorDialog open={errorPopupOpen} errorMessage={errorMessage} onClose={() => setErrorPopupOpen(false)} />
    </form>
  );
});

VendingShiftReportDynamicForm.displayName = 'VendingShiftReportDynamicForm';

export default VendingShiftReportDynamicForm;
